/**
 * Constants and ABIs for direct DEX pool swapping.
 * Phase 1: EVM V2/V3 via Router direct calls
 * Phase 2: Solana via Jupiter with DEX whitelist
 */

// ─── EVM Router Addresses ───

export const V2_ROUTER_ADDRESSES: Record<string, Record<string, string>> = {
  bsc: {
    pancakeswap: '0x10ED43C718714eb63d5aA57B78B54704E256024E',
  },
  eth: {
    uniswap: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
  },
}

export const V3_SWAP_ROUTER_ADDRESSES: Record<string, Record<string, string>> = {
  bsc: {
    pancakeswap: '0x1b81D678ffb9C0263b24A97847620C99d213eB14',
  },
  eth: {
    uniswap: '0xE592427A0AEce92De3Edee1F18E0157C05861564',
  },
  base: {
    uniswap: '0x2626664c2603336E57B271c5C0b26F421741e481',
  },
}

export const V3_QUOTER_ADDRESSES: Record<string, Record<string, string>> = {
  bsc: {
    pancakeswap: '0xB048Bbc1Ee6b0D8E65b86e9c5c8b35B143B06f4d',
  },
  eth: {
    uniswap: '0x61fFE014bA17989E743c5F6cB21bF969dc0b83B7',
  },
  base: {
    uniswap: '0x3d4e44Eb1374240CE5F1B871ab261CD16335B521',
  },
}

// Minimal UniswapV2 Router ABI
export const V2_ROUTER_ABI = [
  {
    inputs: [
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
      { internalType: 'uint256', name: 'amountOutMin', type: 'uint256' },
      { internalType: 'address[]', name: 'path', type: 'address[]' },
      { internalType: 'address', name: 'to', type: 'address' },
      { internalType: 'uint256', name: 'deadline', type: 'uint256' },
    ],
    name: 'swapExactTokensForTokens',
    outputs: [{ internalType: 'uint256[]', name: 'amounts', type: 'uint256[]' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountOutMin', type: 'uint256' },
      { internalType: 'address[]', name: 'path', type: 'address[]' },
      { internalType: 'address', name: 'to', type: 'address' },
      { internalType: 'uint256', name: 'deadline', type: 'uint256' },
    ],
    name: 'swapExactETHForTokens',
    outputs: [{ internalType: 'uint256[]', name: 'amounts', type: 'uint256[]' }],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
      { internalType: 'uint256', name: 'amountOutMin', type: 'uint256' },
      { internalType: 'address[]', name: 'path', type: 'address[]' },
      { internalType: 'address', name: 'to', type: 'address' },
      { internalType: 'uint256', name: 'deadline', type: 'uint256' },
    ],
    name: 'swapExactTokensForETH',
    outputs: [{ internalType: 'uint256[]', name: 'amounts', type: 'uint256[]' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
]

// Minimal V3 SwapRouter ABI (exactInputSingle)
export const V3_SWAP_ROUTER_ABI = [
  {
    inputs: [
      {
        components: [
          { internalType: 'address', name: 'tokenIn', type: 'address' },
          { internalType: 'address', name: 'tokenOut', type: 'address' },
          { internalType: 'uint24', name: 'fee', type: 'uint24' },
          { internalType: 'address', name: 'recipient', type: 'address' },
          { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
          { internalType: 'uint256', name: 'amountOutMinimum', type: 'uint256' },
          { internalType: 'uint160', name: 'sqrtPriceLimitX96', type: 'uint160' },
        ],
        internalType: 'struct ISwapRouter.ExactInputSingleParams',
        name: 'params',
        type: 'tuple',
      },
    ],
    name: 'exactInputSingle',
    outputs: [{ internalType: 'uint256', name: 'amountOut', type: 'uint256' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
]

// Minimal V3 Pool ABI (to fetch fee tier)
export const V3_POOL_ABI = [
  {
    inputs: [],
    name: 'fee',
    outputs: [{ internalType: 'uint24', name: '', type: 'uint24' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'token0',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'token1',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
]

// ─── DEX Type Detection ───

export type DexPoolType = 'evm-v2' | 'evm-v3' | 'solana-jupiter'

export function detectDexPoolType(dex: string): DexPoolType | null {
  const d = dex.toLowerCase()
  // EVM V2 (internal parser names + DexScreener ids)
  if (
    d === 'pancakev2' ||
    d === '_dammv1parser' ||
    d === '_dammv2parser' ||
    d === 'pancakeswap-v2' ||
    d === 'uniswap-v2' ||
    d === 'sushiswap' ||
    d === 'sushiswap-v2' ||
    d === 'quickswap' ||
    d === 'quickswap-v2' ||
    d === 'traderjoe' ||
    d === 'traderjoe-v2' ||
    d === 'camelot' ||
    d === 'camelot-v2' ||
    d === 'aerodrome' ||
    d === 'aerodrome-v2' ||
    d === 'shadow-exchange' ||
    d === 'shadow'
  ) {
    return 'evm-v2'
  }
  // EVM V3 (internal parser names + DexScreener ids)
  if (
    d === 'pancakev3' ||
    d === 'pancakev4' ||
    d === 'univ4' ||
    d === 'uniswap-v3' ||
    d === 'pancakeswap-v3' ||
    d === 'sushiswap-v3' ||
    d === 'quickswap-v3' ||
    d === 'traderjoe-v3' ||
    d === 'camelot-v3' ||
    d === 'aerodrome-v3'
  ) {
    return 'evm-v3'
  }
  // Solana (internal parser names + DexScreener ids)
  if (
    d === '_ammv4parser' ||
    d === '_cpmmparser' ||
    d === '_clmmparser' ||
    d === '_whirlpoolparser' ||
    d === '_dlmmparser' ||
    d === '_pumpswapparser' ||
    d === '_bondingcurveparser' ||
    d === 'raydium' ||
    d === 'orca' ||
    d === 'meteora' ||
    d === 'meteora-dlmm' ||
    d === 'pumpfun' ||
    d === 'pumpswap' ||
    d === 'lifinity' ||
    d === 'phoenix'
  ) {
    return 'solana-jupiter'
  }
  return null
}

export function getDexNameForRouter(dex: string, chain: string): string | null {
  const d = dex.toLowerCase()
  const c = chain.toLowerCase()

  if (
    d === 'pancakev2' ||
    d === 'pancakev3' ||
    d === 'pancakev4' ||
    d === 'pancakeswap-v2' ||
    d === 'pancakeswap-v3'
  ) {
    return 'pancakeswap'
  }
  if (d === 'univ4' || d === 'uniswap-v2' || d === 'uniswap-v3') return 'uniswap'
  if (d === '_dammv1parser' || d === '_dammv2parser') {
    // fallback: try to guess from chain
    if (c === 'bsc') return 'pancakeswap'
    if (c === 'eth' || c === 'base') return 'uniswap'
  }
  // Many V2/V3 DEXes share Uniswap router interface; map them to uniswap on Base/Eth
  if (
    d === 'sushiswap' ||
    d === 'sushiswap-v2' ||
    d === 'sushiswap-v3' ||
    d === 'quickswap' ||
    d === 'quickswap-v2' ||
    d === 'quickswap-v3' ||
    d === 'traderjoe' ||
    d === 'traderjoe-v2' ||
    d === 'traderjoe-v3' ||
    d === 'camelot' ||
    d === 'camelot-v2' ||
    d === 'camelot-v3' ||
    d === 'aerodrome' ||
    d === 'aerodrome-v2' ||
    d === 'aerodrome-v3' ||
    d === 'shadow-exchange' ||
    d === 'shadow'
  ) {
    if (c === 'bsc') return 'pancakeswap'
    if (c === 'eth' || c === 'base') return 'uniswap'
  }
  return null
}

// ─── Solana Jupiter DEX Whitelist Mapping ───

export function getJupiterDexName(dex: string): string | null {
  const d = dex.toLowerCase()
  switch (d) {
    case '_ammv4parser':
    case '_cpmmparser':
    case '_clmmparser':
    case 'raydium':
      return 'Raydium'
    case '_whirlpoolparser':
    case 'orca':
      return 'Orca'
    case '_dlmmparser':
    case 'meteora':
    case 'meteora-dlmm':
      return 'Meteora'
    case '_pumpswapparser':
    case 'pumpswap':
      return 'PumpSwap'
    case 'lifinity':
      return 'Lifinity'
    case 'phoenix':
      return 'Phoenix'
    case '_bondingcurveparser':
    case 'pumpfun':
      // Bonding curve на Pump.fun обычно не доступен через Jupiter,
      // но после миграции токен торгуется на PumpSwap/Raydium.
      // Попробуем без фильтра (null)
      return null
    default:
      return null
  }
}

// V3 Quoter ABI (quoteExactInputSingle)
export const V3_QUOTER_ABI = [
  {
    inputs: [
      { internalType: 'address', name: 'tokenIn', type: 'address' },
      { internalType: 'address', name: 'tokenOut', type: 'address' },
      { internalType: 'uint24', name: 'fee', type: 'uint24' },
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
      { internalType: 'uint160', name: 'sqrtPriceLimitX96', type: 'uint160' },
    ],
    name: 'quoteExactInputSingle',
    outputs: [{ internalType: 'uint256', name: 'amountOut', type: 'uint256' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
]

// V2 Router view function ABI (getAmountsOut)
export const V2_ROUTER_VIEW_ABI = [
  {
    inputs: [
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
      { internalType: 'address[]', name: 'path', type: 'address[]' },
    ],
    name: 'getAmountsOut',
    outputs: [{ internalType: 'uint256[]', name: 'amounts', type: 'uint256[]' }],
    stateMutability: 'view',
    type: 'function',
  },
]

// USDT addresses per chain (lowercase)
export const USDT_ADDRESSES: Record<string, string> = {
  eth: '0xdac17f958d2ee523a2206206994597c13d831ec7',
  bsc: '0x55d398326f99059ff775485246999027b3197955',
  base: '0xfde4c96c8593536e31f229ea8f37b2ada2699bb2',
}

export function getUsdtAddress(chain: string): string | null {
  return USDT_ADDRESSES[chain.toLowerCase()] ?? null
}

// ─── ERC20 Approve ABI fragment ───

export const ERC20_APPROVE_ABI = [
  {
    constant: false,
    inputs: [
      { name: '_spender', type: 'address' },
      { name: '_value', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    payable: false,
    stateMutability: 'nonpayable',
    type: 'function',
  },
]
